// crm-b2b-blue-theme.ts
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import {color} from "chart.js/helpers";

const CRMEnterprisePreset = definePreset(Aura, {
  primitive: {
    borderRadius: {
      none: '0',
      xs: '4px',
      sm: '6px',
      md: '8px',
      lg: '12px',
      xl: '16px'
    },
    borderSection: '1px solid var(--p-surface-300)',
    spacing: {
      none: '0',
      xs: '8px',
      sm: '16px',
      md: '24px',
      lg: '32px',
      xl: '48px'
    },
    blue: {
      50: '#f4f8fb',
      100: '#e8f0f7',
      200: '#d3e1ef',
      300: '#b4cde3',
      400: '#89aecf',
      500: '#5f88af',
      600: '#496f95',
      700: '#3b5a78',
      800: '#314a63',
      900: '#293d52',
      950: '#182434'
    },


    green: {
      50: '#f4fbf7',
      100: '#e6f5ec',
      200: '#c7e7d4',
      300: '#9fd2b2',
      400: '#73b58d',
      500: '#4f966f',
      600: '#3f7d5b',
      700: '#33654a',
      800: '#2b523d',
      900: '#244334',
      950: '#13261d'
    },
    yellow: {
      50: '#fffaf0',
      100: '#fdf1d8',
      200: '#f6dfae',
      300: '#edc97d',
      400: '#ddb056',
      500: '#c39238',
      600: '#a9782e',
      700: '#875f26',
      800: '#6d4c21',
      900: '#593f1d',
      950: '#32220f'
    },
    red: {
      50: '#fdf6f6',
      100: '#faeaea',
      200: '#f3d1d1',
      300: '#e7adad',
      400: '#d88383',
      500: '#be5f5f',
      600: '#a04d4d',
      700: '#833f3f',
      800: '#6d3636',
      900: '#5b3131',
      950: '#321818'
    }
  },

  semantic: {
    transitionDuration: '160ms',
    focusRing: {
      width: '2px',
      style: 'solid',
      color: '{primary.400}',
      offset: '2px',
      shadow: '0 0 0 4px color-mix(in srgb, {primary.500}, transparent 82%)'
    },
    disabledOpacity: '0.55',
    iconSize: '1rem',
    anchorGutter: '4px',

    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}'
    },

    formField: {
      paddingX: '1rem',
      paddingY: '0.5rem',
      sm: {
        fontSize: '0.875rem',
        paddingX: '0.75rem',
        paddingY: '0.5rem'
      },
      lg: {
        fontSize: '1rem',
        paddingX: '1rem',
        paddingY: '0.75rem'
      },
      borderRadius: '{border.radius.sm}',
      focusRing: {
        width: '0',
        style: 'none',
        color: 'transparent',
        offset: '0',
        shadow: 'none'
      },
      transitionDuration: '{transition.duration}'
    },

    list: {
      padding: '0.5rem',
      gap: '0.25rem',
      header: {
        padding: '0.5rem 1rem'
      },
      option: {
        padding: '0.625rem 0.875rem',
        borderRadius: '{border.radius.md}'
      },
      optionGroup: {
        padding: '0.625rem 0.875rem',
        fontWeight: '600'
      }
    },

    content: {
      borderRadius: '{border.radius.md}'
    },

    mask: {
      transitionDuration: '180ms'
    },

    navigation: {
      list: {
        padding: '0.5rem',
        gap: '0.25rem'
      },
      item: {
        padding: '0.625rem 0.875rem',
        borderRadius: '{border.radius.md}',
        gap: '0.625rem'
      },
      submenuLabel: {
        padding: '0.5rem 0.875rem',
        fontWeight: '600'
      },
      submenuIcon: {
        size: '0.875rem'
      }
    },

    overlay: {
      select: {
        borderRadius: '{border.radius.md}',
        shadow: '0 12px 28px rgba(15, 23, 42, 0.10), 0 2px 8px rgba(15, 23, 42, 0.06)'
      },
      popover: {
        borderRadius: '{border.radius.md}',
        padding: '0.75rem',
        shadow: '0 16px 32px rgba(15, 23, 42, 0.12), 0 2px 8px rgba(15, 23, 42, 0.06)'
      },
      modal: {
        borderRadius: '{border.radius.lg}',
        padding: '1.5rem',
        shadow: '0 24px 60px rgba(15, 23, 42, 0.18), 0 8px 24px rgba(15, 23, 42, 0.10)'
      },
      navigation: {
        shadow: '0 16px 32px rgba(15, 23, 42, 0.12), 0 2px 8px rgba(15, 23, 42, 0.06)'
      }
    },

    colorScheme: {
      light: {
        surface: {
          0: '#fcfdff',
          50: '#f8fafc',
          100: '#f1f5f9',
          200: '#e2e8f0',
          300: '#cbd5e1',
          400: '#94a3b8',
          500: '#64748b',
          600: '#475569',
          700: '#334155',
          800: '#1e293b',
          900: '#0f172a',
          950: '#020617'
        },
        primary: {
          color: '{primary.500}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.600}',
          activeColor: '{primary.700}'
        },
        highlight: {
          background: 'color-mix(in srgb, {primary.500}, transparent 92%)',
          focusBackground: 'color-mix(in srgb, {primary.500}, transparent 86%)',
          color: '{primary.700}',
          focusColor: '{primary.800}'
        },
        mask: {
          background: 'rgba(15, 23, 42, 0.18)',
          color: '{surface.200}'
        },
        formField: {
          background: '#ffffff',
          disabledBackground: '{surface.100}',
          filledBackground: '#ffffff',
          filledHoverBackground: '#ffffff',
          filledFocusBackground: '#ffffff',
          borderColor: '{surface.300}',
          hoverBorderColor: '{surface.400}',
          focusBorderColor: '{primary.500}',
          invalidBorderColor: '{red.400}',
          color: '{surface.800}',
          disabledColor: '{surface.500}',
          placeholderColor: '{surface.400}',
          invalidPlaceholderColor: '{red.500}',
          floatLabelColor: '{surface.500}',
          floatLabelFocusColor: '{primary.600}',
          floatLabelActiveColor: '{surface.500}',
          floatLabelInvalidColor: '{red.500}',
          iconColor: '{surface.400}',
          shadow: '0 1px 2px rgba(15, 23, 42, 0.03)'
        },
        text: {
          color: '{surface.800}',
          hoverColor: '{surface.900}',
          mutedColor: '{surface.500}',
          hoverMutedColor: '{surface.600}'
        },
        content: {
          background: '{surface.0}',
          hoverBackground: '{surface.50}',
          borderColor: '{surface.200}',
          color: '{text.color}',
          hoverColor: '{text.hover.color}'
        },
        overlay: {
          select: {
            background: '{surface.0}',
            borderColor: '{surface.200}',
            color: '{text.color}'
          },
          popover: {
            background: '{surface.0}',
            borderColor: '{surface.200}',
            color: '{text.color}'
          },
          modal: {
            background: '{surface.0}',
            borderColor: '{surface.200}',
            color: '{text.color}'
          }
        },
        list: {
          option: {
            focusBackground: '{surface.100}',
            selectedBackground: 'color-mix(in srgb, {primary.500}, transparent 90%)',
            selectedFocusBackground: 'color-mix(in srgb, {primary.500}, transparent 84%)',
            color: '{text.color}',
            focusColor: '{text.hover.color}',
            selectedColor: '{primary.700}',
            selectedFocusColor: '{primary.800}',
            icon: {
              color: '{surface.400}',
              focusColor: '{surface.500}'
            }
          },
          optionGroup: {
            background: 'transparent',
            color: '{text.muted.color}'
          }
        },
        navigation: {
          item: {
            focusBackground: '{surface.100}',
            activeBackground: 'color-mix(in srgb, {primary.500}, transparent 92%)',
            color: '{text.color}',
            focusColor: '{text.hover.color}',
            activeColor: '{primary.700}',
            icon: {
              color: '{surface.400}',
              focusColor: '{surface.500}',
              activeColor: '{primary.600}'
            }
          },
          submenuLabel: {
            background: 'transparent',
            color: '{text.muted.color}'
          },
          submenuIcon: {
            color: '{surface.400}',
            focusColor: '{surface.500}',
            activeColor: '{primary.600}'
          }
        }
      },

      dark: {
        surface: {
          0: '#ffffff',
          50: '#f5f7fb',
          100: '#e7ebf2',
          200: '#cfd6e2',
          300: '#aab5c7',
          400: '#7f8ba0',
          500: '#5f6b81',
          600: '#445065',
          700: '#31384a',
          800: '#1e2431',
          900: '#121722',
          950: '#0a0d14'
        },
        primary: {
          color: '{primary.400}',
          contrastColor: '{surface.950}',
          hoverColor: '{primary.300}',
          activeColor: '{primary.200}'
        },
        highlight: {
          background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
          focusBackground: 'color-mix(in srgb, {primary.400}, transparent 76%)',
          color: 'rgba(255, 255, 255, 0.92)',
          focusColor: 'rgba(255, 255, 255, 0.96)'
        },
        mask: {
          background: 'rgba(2, 6, 23, 0.56)',
          color: '{surface.200}'
        },
        formField: {
          background: '{surface.900}',
          disabledBackground: '{surface.800}',
          filledBackground: '{surface.900}',
          filledHoverBackground: '{surface.900}',
          filledFocusBackground: '{surface.900}',
          borderColor: '{surface.700}',
          hoverBorderColor: '{surface.600}',
          focusBorderColor: '{primary.400}',
          invalidBorderColor: '{red.400}',
          color: 'rgba(255, 255, 255, 0.92)',
          disabledColor: '{surface.400}',
          placeholderColor: '{surface.400}',
          invalidPlaceholderColor: '{red.300}',
          floatLabelColor: '{surface.400}',
          floatLabelFocusColor: '{primary.300}',
          floatLabelActiveColor: '{surface.400}',
          floatLabelInvalidColor: '{red.300}',
          iconColor: '{surface.400}',
          shadow: '0 1px 2px 0 rgba(0, 0, 0, 0.18)'
        },
        text: {
          color: 'rgba(255, 255, 255, 0.92)',
          hoverColor: '#ffffff',
          mutedColor: '{surface.400}',
          hoverMutedColor: '{surface.300}'
        },
        content: {
          background: '{surface.900}',
          hoverBackground: '{surface.800}',
          borderColor: '{surface.700}',
          color: '{text.color}',
          hoverColor: '{text.hover.color}'
        },
        overlay: {
          select: {
            background: '{surface.900}',
            borderColor: '{surface.700}',
            color: '{text.color}'
          },
          popover: {
            background: '{surface.900}',
            borderColor: '{surface.700}',
            color: '{text.color}'
          },
          modal: {
            background: '{surface.900}',
            borderColor: '{surface.700}',
            color: '{text.color}'
          }
        },
        list: {
          option: {
            focusBackground: '{surface.800}',
            selectedBackground: 'color-mix(in srgb, {primary.400}, transparent 86%)',
            selectedFocusBackground: 'color-mix(in srgb, {primary.400}, transparent 78%)',
            color: '{text.color}',
            focusColor: '#ffffff',
            selectedColor: '#ffffff',
            selectedFocusColor: '#ffffff',
            icon: {
              color: '{surface.400}',
              focusColor: '{surface.300}'
            }
          },
          optionGroup: {
            background: 'transparent',
            color: '{text.muted.color}'
          }
        },
        navigation: {
          item: {
            focusBackground: '{surface.800}',
            activeBackground: 'color-mix(in srgb, {primary.400}, transparent 88%)',
            color: '{text.color}',
            focusColor: '#ffffff',
            activeColor: '#ffffff',
            icon: {
              color: '{surface.400}',
              focusColor: '{surface.300}',
              activeColor: '{primary.300}'
            }
          },
          submenuLabel: {
            background: 'transparent',
            color: '{text.muted.color}'
          },
          submenuIcon: {
            color: '{surface.400}',
            focusColor: '{surface.300}',
            activeColor: '{primary.300}'
          }
        }
      }
    }
  },

  components: {
    splitter: {
      root: {
        borderColor: '{surface.300}',
      }
    },
    button: {
      root: {
        borderRadius: '{border.radius.md}',
        paddingX: '1rem',
        paddingY: '0.625rem',
        gap: '0.5rem',
        label: {
          fontWeight: '600'
        },
        raisedShadow: '0 1px 2px rgba(15, 23, 42, 0.08), 0 8px 24px rgba(15, 23, 42, 0.10)',
        focusRing: {
          width: '{focus.ring.width}',
          style: '{focus.ring.style}',
          //color: '{focus.ring.color}',
          offset: '{focus.ring.offset}',
          //shadow: '{focus.ring.shadow}'
        }
      },
      colorScheme: {
        light: {
          root: {
            primary: {
              background: '{primary.500}',
              hoverBackground: '{primary.600}',
              activeBackground: '{primary.700}',
              borderColor: '{primary.500}',
              hoverBorderColor: '{primary.600}',
              activeBorderColor: '{primary.700}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff'
            },
            secondary: {
              background: '{surface.0}',
              hoverBackground: '{surface.50}',
              activeBackground: '{surface.100}',
              borderColor: '{surface.200}',
              hoverBorderColor: '{surface.300}',
              activeBorderColor: '{surface.400}',
              color: '{surface.700}',
              hoverColor: '{surface.800}',
              activeColor: '{surface.900}'
            },
            success: {
              background: '{green.500}',
              hoverBackground: '{green.600}',
              activeBackground: '{green.700}',
              borderColor: '{green.500}',
              hoverBorderColor: '{green.600}',
              activeBorderColor: '{green.700}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff'
            },
            warn: {
              background: '{yellow.500}',
              hoverBackground: '{yellow.600}',
              activeBackground: '{yellow.700}',
              borderColor: '{yellow.500}',
              hoverBorderColor: '{yellow.600}',
              activeBorderColor: '{yellow.700}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff'
            },
            danger: {
              background: '{red.500}',
              hoverBackground: '{red.600}',
              activeBackground: '{red.700}',
              borderColor: '{red.500}',
              hoverBorderColor: '{red.600}',
              activeBorderColor: '{red.700}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff'
            },
            contrast: {
              background: '{surface.900}',
              hoverBackground: '{surface.800}',
              activeBackground: '{surface.700}',
              borderColor: '{surface.900}',
              hoverBorderColor: '{surface.800}',
              activeBorderColor: '{surface.700}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff'
            }
          }
        },
        dark: {
          root: {
            primary: {
              background: '{primary.400}',
              hoverBackground: '{primary.300}',
              activeBackground: '{primary.200}',
              borderColor: '{primary.400}',
              hoverBorderColor: '{primary.300}',
              activeBorderColor: '{primary.200}',
              color: '{surface.950}',
              hoverColor: '{surface.950}',
              activeColor: '{surface.950}'
            },
            secondary: {
              background: '{surface.900}',
              hoverBackground: '{surface.800}',
              activeBackground: '{surface.700}',
              borderColor: '{surface.700}',
              hoverBorderColor: '{surface.600}',
              activeBorderColor: '{surface.500}',
              color: '{surface.100}',
              hoverColor: '#ffffff',
              activeColor: '#ffffff'
            },
            success: {
              background: '{green.400}',
              hoverBackground: '{green.300}',
              activeBackground: '{green.200}',
              borderColor: '{green.400}',
              hoverBorderColor: '{green.300}',
              activeBorderColor: '{green.200}',
              color: '{surface.950}',
              hoverColor: '{surface.950}',
              activeColor: '{surface.950}'
            },
            warn: {
              background: '{yellow.400}',
              hoverBackground: '{yellow.300}',
              activeBackground: '{yellow.200}',
              borderColor: '{yellow.400}',
              hoverBorderColor: '{yellow.300}',
              activeBorderColor: '{yellow.200}',
              color: '{surface.950}',
              hoverColor: '{surface.950}',
              activeColor: '{surface.950}'
            },
            danger: {
              background: '{red.400}',
              hoverBackground: '{red.300}',
              activeBackground: '{red.200}',
              borderColor: '{red.400}',
              hoverBorderColor: '{red.300}',
              activeBorderColor: '{red.200}',
              color: '{surface.950}',
              hoverColor: '{surface.950}',
              activeColor: '{surface.950}'
            },
            contrast: {
              background: '{surface.0}',
              hoverBackground: '{surface.100}',
              activeBackground: '{surface.200}',
              borderColor: '{surface.0}',
              hoverBorderColor: '{surface.100}',
              activeBorderColor: '{surface.200}',
              color: '{surface.950}',
              hoverColor: '{surface.950}',
              activeColor: '{surface.950}'
            }
          }
        }
      }
    },

    card: {
      root: {
        background: '{content.background}',
        borderRadius: '{border.radius.md}',
        color: '{content.color}',
        shadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 12px 32px rgba(15, 23, 42, 0.06)'
      },
      body: {
        padding: '1.5rem',
        gap: '0.75rem'
      },
      title: {
        fontSize: '1.25rem',
        fontWeight: '600'
      },
      subtitle: {
        color: '{text.muted.color}'
      }
    },

    tooltip: {
      root: {
        maxWidth: '25rem'
      }
    },

    select: {
      root: {
        borderRadius: '{border.radius.xs}',
      },
      dropdown: {
        width: '2.75rem',
        color: '{form.field.icon.color}'
      },
      overlay: {
        background: '{overlay.select.background}',
        borderColor: '{overlay.select.border.color}',
        borderRadius: '{overlay.select.border.radius}',
        color: '{overlay.select.color}',
        shadow: '{overlay.select.shadow}'
      },
      list: {
        padding: '{list.padding}',
        gap: '{list.gap}'
      },
      option: {
        focusBackground: '{list.option.focus.background}',
        selectedBackground: '{list.option.selected.background}',
        selectedFocusBackground: '{list.option.selected.focus.background}',
        color: '{list.option.color}',
        focusColor: '{list.option.focus.color}',
        selectedColor: '{list.option.selected.color}',
        selectedFocusColor: '{list.option.selected.focus.color}',
        padding: '{list.option.padding}',
        borderRadius: '{list.option.border.radius}'
      }
    },

    menu: {
      root: {
        background: '{content.background}',
        borderColor: '{content.border.color}',
        color: '{content.color}',
        borderRadius: '{content.border.radius}',
        shadow: '{overlay.navigation.shadow}'
      },
      list: {
        padding: '{navigation.list.padding}',
        gap: '{navigation.list.gap}'
      },
      item: {
        focusBackground: '{navigation.item.focus.background}',
        color: '{navigation.item.color}',
        focusColor: '{navigation.item.focus.color}',
        padding: '{navigation.item.padding}',
        borderRadius: '{navigation.item.border.radius}',
        gap: '{navigation.item.gap}',
        icon: {
          color: '{navigation.item.icon.color}',
          focusColor: '{navigation.item.icon.focus.color}'
        }
      },
      submenuLabel: {
        padding: '{navigation.submenu.label.padding}',
        fontWeight: '{navigation.submenu.label.font.weight}',
        background: '{navigation.submenu.label.background}',
        color: '{navigation.submenu.label.color}'
      }
    },

    menubar: {
      root: {
        background: '{content.background}',
        borderColor: '{content.border.color}',
        borderRadius: '{border.radius.lg}',
        color: '{content.color}',
        gap: '0.5rem',
        padding: '0.5rem 0.75rem'
      },
      baseItem: {
        borderRadius: '{border.radius.md}',
        padding: '{navigation.item.padding}'
      },
      item: {
        focusBackground: '{navigation.item.focus.background}',
        activeBackground: '{navigation.item.active.background}',
        color: '{navigation.item.color}',
        focusColor: '{navigation.item.focus.color}',
        activeColor: '{navigation.item.active.color}',
        padding: '{navigation.item.padding}',
        borderRadius: '{navigation.item.border.radius}',
        gap: '{navigation.item.gap}',
        icon: {
          color: '{navigation.item.icon.color}',
          focusColor: '{navigation.item.icon.focus.color}',
          activeColor: '{navigation.item.icon.active.color}'
        }
      },
      submenu: {
        padding: '{navigation.list.padding}',
        gap: '{navigation.list.gap}',
        background: '{content.background}',
        borderColor: '{content.border.color}',
        borderRadius: '{border.radius.lg}',
        shadow: '{overlay.navigation.shadow}'
      }
    },

    paginator: {
      root: {
        padding: '0.75rem 1rem',
        gap: '0.25rem',
        borderRadius: '{content.border.radius}',
        background: '{content.background}',
        color: '{content.color}'
      },
      navButton: {
        background: 'transparent',
        hoverBackground: '{content.hover.background}',
        selectedBackground: '{highlight.background}',
        color: '{text.muted.color}',
        hoverColor: '{text.hover.muted.color}',
        selectedColor: '{highlight.color}',
        width: '2.25rem',
        height: '2.25rem',
        borderRadius: '999px'
      },
      currentPageReport: {
        color: '{text.muted.color}'
      }
    },

    datatable: {
      root: {
        borderColor: '{content.border.color}'
      },
      header: {
        background: '{surface.50}',
        borderColor: '{content.border.color}',
        color: '{content.color}',
        borderWidth: '0 0 1px 0',
        padding: '1rem'
      },
      headerCell: {
        background: '{surface.50}',
        hoverBackground: '{surface.100}',
        selectedBackground: '{highlight.background}',
        borderColor: '{content.border.color}',
        color: '{content.color}',
        hoverColor: '{text.hover.color}',
        selectedColor: '{highlight.color}',
        gap: '0.5rem',
        padding: '0.875rem 1rem'
      },
      columnTitle: {
        fontWeight: '600'
      },
      row: {
        background: '{content.background}',
        hoverBackground: 'color-mix(in srgb, {primary.500}, transparent 96%)',
        selectedBackground: 'color-mix(in srgb, {primary.500}, transparent 90%)',
        color: '{content.color}',
        hoverColor: '{text.hover.color}',
        selectedColor: '{primary.700}'
      },
      bodyCell: {
        borderColor: '{content.border.color}',
        padding: '0.875rem 1rem'
      },
      footer: {
        background: '{surface.50}',
        borderColor: '{content.border.color}',
        color: '{content.color}',
        borderWidth: '1px 0 0 0',
        padding: '1rem'
      }
    },

    dialog: {
      root: {
        background: '{overlay.modal.background}',
        borderColor: '{overlay.modal.border.color}',
        color: '{overlay.modal.color}',
        borderRadius: '{overlay.modal.border.radius}',
        shadow: '{overlay.modal.shadow}'
      },
      header: {
        padding: '{overlay.modal.padding}',
        gap: '0.5rem'
      },
      title: {
        fontSize: '1.25rem',
        fontWeight: '600'
      },
      content: {
        padding: '0 {overlay.modal.padding} {overlay.modal.padding} {overlay.modal.padding}'
      },
      footer: {
        padding: '0 {overlay.modal.padding} {overlay.modal.padding} {overlay.modal.padding}',
        gap: '0.75rem'
      }
    },

    message: {
      root: {
        borderRadius: '{border.radius.md}',
        borderWidth: '1px'
      },
      content: {
        padding: '0.75rem 1rem',
        gap: '0.625rem'
      },
      text: {
        fontSize: '0.9375rem',
        fontWeight: '500'
      },
      closeButton: {
        width: '2rem',
        height: '2rem',
        borderRadius: '999px'
      },
      colorScheme: {
        light: {
          info: {
            background: 'color-mix(in srgb, {primary.50}, transparent 4%)',
            borderColor: 'color-mix(in srgb, {primary.500}, transparent 72%)',
            color: '{primary.700}'
          },
          success: {
            background: 'color-mix(in srgb, {green.50}, transparent 4%)',
            borderColor: 'color-mix(in srgb, {green.500}, transparent 72%)',
            color: '{green.700}'
          },
          warn: {
            background: 'color-mix(in srgb, {yellow.50}, transparent 4%)',
            borderColor: 'color-mix(in srgb, {yellow.500}, transparent 68%)',
            color: '{yellow.800}'
          },
          error: {
            background: 'color-mix(in srgb, {red.50}, transparent 4%)',
            borderColor: 'color-mix(in srgb, {red.500}, transparent 72%)',
            color: '{red.700}'
          },
          secondary: {
            background: '{surface.50}',
            borderColor: '{surface.200}',
            color: '{surface.700}'
          },
          contrast: {
            background: '{surface.900}',
            borderColor: '{surface.800}',
            color: '{surface.50}'
          }
        },
        dark: {
          info: {
            background: 'color-mix(in srgb, {primary.500}, transparent 84%)',
            borderColor: 'color-mix(in srgb, {primary.400}, transparent 62%)',
            color: '{primary.200}'
          },
          success: {
            background: 'color-mix(in srgb, {green.500}, transparent 84%)',
            borderColor: 'color-mix(in srgb, {green.400}, transparent 62%)',
            color: '{green.200}'
          },
          warn: {
            background: 'color-mix(in srgb, {yellow.500}, transparent 84%)',
            borderColor: 'color-mix(in srgb, {yellow.400}, transparent 62%)',
            color: '{yellow.200}'
          },
          error: {
            background: 'color-mix(in srgb, {red.500}, transparent 84%)',
            borderColor: 'color-mix(in srgb, {red.400}, transparent 62%)',
            color: '{red.200}'
          },
          secondary: {
            background: '{surface.800}',
            borderColor: '{surface.700}',
            color: '{surface.200}'
          },
          contrast: {
            background: '{surface.0}',
            borderColor: '{surface.200}',
            color: '{surface.950}'
          }
        }
      }
    }
  }
});

export default CRMEnterprisePreset;
